import { useState, useRef, useEffect } from 'react';
import { User, Search, X, ChevronDown, Users, Zap } from 'lucide-react';

/**
 * UserSelector
 *
 * Dual-mode user picker for the package filter:
 * - Dropdown for quick selection when you know the user
 * - Search/filter field for when there are many users
 * - Toggle between "all users" vs "users with active packages only"
 *
 * The selected user_id is passed back via onChange(userId).
 */
export function UserSelector({ users, usersLoading, activeUsersOnly, setActiveUsersOnly, selectedUserId, selectedProviderId, onChange, onSearch }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownUp, setDropdownUp] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => onSearch?.(searchTerm), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, onSearch]);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate if dropdown should render upward to avoid overflow
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320; // max-h-80 = 320px
      setDropdownUp(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    }
  }, [isOpen]);

  // Filter items (users + providers) by search term
  const filteredItems = (users || []).filter(item => {
    const searchLower = searchTerm.toLowerCase();
    if (item.type === 'provider') {
      return (item.name || '').toLowerCase().includes(searchLower) ||
             (item.email || '').toLowerCase().includes(searchLower);
    } else {
      return (item.username || '').toLowerCase().includes(searchLower) ||
             (item.email || '').toLowerCase().includes(searchLower);
    }
  });

  // Find currently selected item
  const selectedItem = (users || []).find(item => 
    (item.type === 'user' && item.id === selectedUserId) ||
    (item.type === 'provider' && item.id === selectedProviderId)
  );

  const handleSelect = (item) => {
    onChange(item); // Pass the full item object so parent can decide user_id vs provider_id
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Toggle for active users only */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => setActiveUsersOnly(prev => !prev)}
          className={`flex items-center gap-1.5 text-xs font-montserrat font-semibold px-2 py-1 rounded-lg transition-colors ${
            activeUsersOnly
              ? 'bg-brand-green/10 text-brand-green border border-brand-green/30'
              : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}
        >
          <Zap size={11} />
          Solo activos
        </button>
        <span className="text-xs text-gray-400 font-montserrat">
          {(users || []).length} usuario{(users || []).length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Selector button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center gap-2 text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 transition-colors text-left"
      >
        <User size={14} className="text-gray-400 shrink-0" />
        {selectedItem ? (
          <span className="flex-1 truncate font-semibold text-gray-700">
            {selectedItem.type === 'provider' ? selectedItem.name : selectedItem.username}
            <span className="font-normal text-gray-400 ml-1">
              ({selectedItem.package_count} paq.{selectedItem.type === 'provider' ? ` · ${selectedItem.store_count} tiendas` : ''})
            </span>
          </span>
        ) : (
          <span className="flex-1 text-gray-400">Seleccionar usuario o proveedor...</span>
        )}
        {(selectedUserId || selectedProviderId) ? (
          <button
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden flex flex-col ${
          dropdownUp ? 'bottom-full mb-1 max-h-80' : 'top-full mt-1 max-h-96'
        }`} style={{ zIndex: 9999 }}>
          {/* Search field */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Buscar usuario..."
                className="w-full pl-8 pr-3 py-1.5 text-sm font-montserrat bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-brand-primary"
                autoFocus
              />
            </div>
          </div>

          {/* User list */}
          <div className="overflow-y-auto flex-1">
            {usersLoading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400 font-montserrat">
                Cargando...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400 font-montserrat">
                {searchTerm ? `Sin resultados para "${searchTerm}"` : 'Sin items'}
              </div>
            ) : (
              filteredItems.map(item => {
                const isSelected = (item.type === 'user' && item.id === selectedUserId) ||
                                   (item.type === 'provider' && item.id === selectedProviderId);
                const isProvider = item.type === 'provider';
                
                return (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${
                      isSelected ? 'bg-brand-primary/5' : ''
                    }`}
                  >
                    <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-brand-primary/20' : isProvider ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                      {isProvider ? (
                        <Users size={13} className={isSelected ? 'text-brand-primary' : 'text-purple-500'} />
                      ) : (
                        <User size={13} className={isSelected ? 'text-brand-primary' : 'text-gray-400'} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`font-montserrat font-semibold text-sm truncate ${
                          isSelected ? 'text-brand-primary' : 'text-gray-700'
                        }`}>
                          {isProvider ? item.name : item.username}
                        </p>
                        {isProvider && (
                          <span className="text-xs bg-purple-100 text-purple-600 font-montserrat font-semibold px-1.5 py-0.5 rounded shrink-0">
                            Proveedor
                          </span>
                        )}
                      </div>
                      <p className="font-montserrat text-xs text-gray-400 truncate">
                        {item.email}
                        {isProvider && ` · ${item.store_count} tiendas`}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <span className={`text-xs font-montserrat font-bold px-2 py-0.5 rounded-full ${
                        isSelected
                          ? 'bg-brand-primary/20 text-brand-primary'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {item.package_count}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer with "All users" option */}
          {(selectedUserId || selectedProviderId) && (
            <div className="border-t border-gray-100 p-2">
              <button
                onClick={handleClear}
                className="w-full flex items-center justify-center gap-1.5 text-sm font-montserrat font-semibold text-gray-500 hover:text-brand-primary py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Users size={14} />
                Ver todos los usuarios
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}